import { useCallback, useEffect, useState } from 'react';
import {
  completeAdminChatFollowup,
  getAdminChatFollowupSummary,
  listAdminChatFollowups,
  promoteAdminChatFollowup,
} from '../../../../api/adminChat';
import { requestApiJson } from '../../../../api/client';
import {
  extractChatFollowupMineCount as extractMineCountFromSummary,
  normalizeChatFollowupMineCountsByChannel,
} from '../../../../utils/adminChatMenuBadges';

const API_URL = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const ABSOLUTE_HTTP_URL_RE = /^https?:\/\//i;
const DATA_URL_RE = /^data:/i;

export const CHAT_FOLLOWUP_AI_TARGET_ID = '__casticart_ai__';
const CHAT_FOLLOWUP_SUMMARY_REFRESH_MS = 2 * 60 * 60 * 1000;

export const CHAT_FOLLOWUP_NOTIFICATION_MODES = Object.freeze({
  all: 'all',
  mine: 'mine',
  off: 'off',
});

export const CHAT_FOLLOWUP_NOTIFICATION_OPTIONS = Object.freeze([
  { mode: CHAT_FOLLOWUP_NOTIFICATION_MODES.all, label: 'すべて通知' },
  { mode: CHAT_FOLLOWUP_NOTIFICATION_MODES.mine, label: '自分の担当だけ通知' },
  { mode: CHAT_FOLLOWUP_NOTIFICATION_MODES.off, label: '通知オフ' },
]);

const EMPTY_SUMMARY = Object.freeze({
  openCount: 0,
  mineCount: 0,
  assignedToMeCount: 0,
  mineCountsByChannel: Object.freeze({
    personal: 0,
    event: 0,
    group: 0,
  }),
  watchCount: 0,
  nudgeCount: 0,
  followupCount: 0,
  escalatedCount: 0,
  updatedAt: '',
});

const toSafeInt = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < 0) return 0;
  return Math.trunc(numeric);
};

const pickFirstText = (...values) => {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const normalized = value.trim();
    if (normalized) return normalized;
  }
  return '';
};

const normalizeApiAssetUrl = (value) => {
  const text = pickFirstText(value);
  if (!text) return '';
  if (ABSOLUTE_HTTP_URL_RE.test(text) || DATA_URL_RE.test(text)) return text;
  if (text.startsWith('/')) {
    return API_URL ? `${API_URL}${text}` : text;
  }
  return text;
};

export const normalizeChatFollowupSummary = (payload = {}) => {
  const source = payload?.summary && typeof payload.summary === 'object' ? payload.summary : payload;
  const stageCounts =
    source?.stageCounts && typeof source.stageCounts === 'object'
      ? source.stageCounts
      : source?.counts && typeof source.counts === 'object'
        ? source.counts
        : {};

  const openCount = toSafeInt(
    source?.openCount ??
      source?.open_count ??
      source?.openTasks ??
      source?.open_tasks ??
      source?.totalOpen ??
      source?.total_open ??
      source?.total ??
      source?.items,
  );
  const mineCountsByChannel = normalizeChatFollowupMineCountsByChannel(source);
  const mineCount = extractMineCountFromSummary({ ...source, mineCountsByChannel });

  return {
    openCount,
    mineCount,
    assignedToMeCount: mineCount,
    mineCountsByChannel,
    watchCount: toSafeInt(stageCounts.watch ?? source?.watchCount ?? source?.watch_count),
    nudgeCount: toSafeInt(stageCounts.nudge ?? source?.nudgeCount ?? source?.nudge_count),
    followupCount: toSafeInt(
      stageCounts.followup ?? source?.followupCount ?? source?.followup_count,
    ),
    escalatedCount: toSafeInt(
      stageCounts.escalated ?? source?.escalatedCount ?? source?.escalated_count,
    ),
    updatedAt: pickFirstText(
      source?.updatedAt,
      source?.updated_at,
      source?.lastEvaluatedAt,
      source?.last_evaluated_at,
    ),
  };
};

export const extractChatFollowupOpenCount = (summary) =>
  normalizeChatFollowupSummary(summary).openCount;

export const extractChatFollowupMineCount = (summary) =>
  normalizeChatFollowupSummary(summary).mineCount;

export const resolveChatFollowupBadgeToneClass = (summary) =>
  extractChatFollowupMineCount(summary) > 0 ? 'badge-tone--alert' : 'badge-tone--ok';

export const fetchAdminChatFollowupSummary = async ({ signal, refresh = false } = {}) => {
  const data = await getAdminChatFollowupSummary(refresh ? { refresh: '1' } : undefined, { signal });
  return normalizeChatFollowupSummary(data || EMPTY_SUMMARY);
};

export const fetchAdminChatFollowupInbox = async ({ limit = 12, signal, refresh = false } = {}) => {
  const data = await listAdminChatFollowups(
    {
      limit,
      ...(refresh ? { refresh: '1' } : {}),
    },
    { signal },
  );
  const items = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data?.tasks)
      ? data.tasks
      : [];

  return {
    summary: normalizeChatFollowupSummary(data || EMPTY_SUMMARY),
    items,
  };
};

export const completeAdminChatFollowupItem = async ({ itemId }) => {
  return completeAdminChatFollowup({ itemId });
};

export const promoteAdminChatFollowupItem = async ({ itemId }) => {
  return promoteAdminChatFollowup({ itemId });
};

export const normalizeChatFollowupNotificationMode = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  return CHAT_FOLLOWUP_NOTIFICATION_OPTIONS.some((option) => option.mode === raw)
    ? raw
    : CHAT_FOLLOWUP_NOTIFICATION_MODES.mine;
};

export const getChatFollowupNotificationLabel = (mode) => {
  const normalized = normalizeChatFollowupNotificationMode(mode);
  return CHAT_FOLLOWUP_NOTIFICATION_OPTIONS.find((option) => option.mode === normalized)?.label || '自分の担当だけ通知';
};

export const normalizeChatFollowupNotificationPreference = (payload = {}) => {
  const source = payload?.preference && typeof payload.preference === 'object' ? payload.preference : payload;
  const mode = normalizeChatFollowupNotificationMode(source?.mode || source?.notificationMode || source?.notification_mode);
  return {
    mode,
    label: source?.label || getChatFollowupNotificationLabel(mode),
    updatedAt: pickFirstText(source?.updatedAt, source?.updated_at),
    isDefault: source?.isDefault === true || source?.is_default === true,
  };
};

export const fetchChatFollowupNotificationPreference = async ({ signal } = {}) => {
  const { data, response, error } = await requestApiJson('/admin/chat-followups/notification-preference', {
    signal,
  });
  if (!response.ok) {
    throw new Error(error?.message || 'AIチャット通知設定を取得できませんでした。');
  }
  return normalizeChatFollowupNotificationPreference(data || {});
};

export const updateChatFollowupNotificationPreference = async ({ mode }) => {
  const normalizedMode = normalizeChatFollowupNotificationMode(mode);
  const { data, response, error } = await requestApiJson('/admin/chat-followups/notification-preference', {
    method: 'PUT',
    body: { mode: normalizedMode },
  });
  if (!response.ok) {
    throw new Error(error?.message || 'AIチャット通知設定を更新できませんでした。');
  }
  return normalizeChatFollowupNotificationPreference(data || {});
};

// v2: 相手/スタッフの実メッセージ抜粋（受信箱の主役）。無ければ従来のメタ文へフォールバック。
export const resolveChatFollowupAnchorExcerpt = (item = {}) =>
  pickFirstText(item?.anchorExcerpt, item?.anchor_excerpt);

export const resolveChatFollowupAnchorSenderLabel = (item = {}) =>
  pickFirstText(item?.anchorSenderLabel, item?.anchor_sender_label);

export const resolveChatFollowupAnchorAt = (item = {}) =>
  pickFirstText(item?.anchorAt, item?.anchor_at);

// v2: 担当推測の根拠ラベル（メンション/直近対応/イベント担当）。断定を避けるため名の後に併記。
export const resolveChatFollowupAssigneeBasisLabel = (item = {}) =>
  pickFirstText(item?.assigneeBasisLabel, item?.assignee_basis_label);

// v2: イベントのフライヤー表示情報（EventFlyerMedia に渡す）。
export const resolveChatFollowupEventFlyer = (item = {}) => {
  const flyer = item?.eventFlyer || item?.event_flyer || null;
  if (!flyer || typeof flyer !== 'object') return null;
  const flyerFrontUrl = normalizeApiAssetUrl(
    pickFirstText(flyer.flyerFrontUrl, flyer.flyer_front_url, flyer.frontUrl, flyer.url),
  );
  const placeholderDesignKey = pickFirstText(flyer.placeholderDesignKey, flyer.placeholder_design_key);
  const placeholderColorKey = pickFirstText(flyer.placeholderColorKey, flyer.placeholder_color_key);
  if (!flyerFrontUrl && !placeholderDesignKey && !placeholderColorKey) return null;
  return { flyerFrontUrl, placeholderDesignKey, placeholderColorKey };
};

export const resolveChatFollowupStage = (item = {}) =>
  pickFirstText(item?.stage, item?.remindStage, item?.level, item?.status).toLowerCase() || 'watch';

export const resolveChatFollowupStageLabel = (stage) => {
  switch (String(stage || '').toLowerCase()) {
    case 'nudge':
      return '軽く確認';
    case 'followup':
      return '要フォロー';
    case 'escalated':
      return '優先対応';
    default:
      return '見守り';
  }
};

export const resolveChatFollowupChannel = (item = {}) =>
  pickFirstText(item?.channel, item?.chatType, item?.scope).toLowerCase() || 'personal';

// 論点台帳方式（issues エンジン）の追加フィールド。旧エンジンでは空になり、UIは自然に何も出さない。
export const resolveChatFollowupKind = (item = {}) =>
  pickFirstText(item?.kind, item?.issueKind, item?.issue_kind).toLowerCase();

export const resolveChatFollowupKindLabel = (kind) => {
  switch (String(kind || '').toLowerCase()) {
    case 'admin_commitment':
      return '約束';
    case 'unanswered_message':
      return '未返信';
    case 'unresolved_issue':
      return '未解決';
    case 'dispute':
      return '要注意';
    default:
      return '';
  }
};

export const resolveChatFollowupDueAt = (item = {}) =>
  pickFirstText(item?.dueAt, item?.due_at);

export const resolveChatFollowupWaitingOn = (item = {}) =>
  pickFirstText(item?.waitingOn, item?.waiting_on).toLowerCase();

export const resolveChatFollowupTargetLabel = (item = {}) => {
  const channel = resolveChatFollowupChannel(item);
  if (channel === 'event') return 'イベントチャット';
  if (channel === 'group') return 'グループチャット';
  return '個別チャット';
};

export const resolveChatFollowupTargetName = (item = {}) =>
  pickFirstText(
    item?.targetName,
    item?.artistName,
    item?.artist_name,
    item?.eventName,
    item?.event_name,
    item?.threadTitle,
    item?.thread_title,
    item?.title,
  ) || '対象チャット';

export const resolveChatFollowupTaskTitle = (item = {}) =>
  pickFirstText(item?.taskTitle, item?.task_title, item?.headline, item?.summary) || '対応確認';

export const resolveChatFollowupInstruction = (item = {}) =>
  pickFirstText(
    item?.nextAction,
    item?.next_action,
    item?.instruction,
    item?.taskBody,
    item?.task_body,
    item?.reason,
    item?.summary,
    item?.contextSummary,
    item?.context_summary,
  );

export const resolveChatFollowupAssigneeName = (item = {}) =>
  pickFirstText(
    item?.assigneeName,
    item?.assignee_name,
    item?.assignedAdminName,
    item?.assigned_admin_name,
    item?.ownerAdminName,
    item?.owner_admin_name,
    item?.assignee?.name,
  );

export const resolveChatFollowupMetaText = (item = {}) =>
  pickFirstText(
    item?.contextNote,
    item?.context_note,
    item?.metaText,
    item?.meta_text,
    item?.latestMessageExcerpt,
    item?.latest_message_excerpt,
    item?.lastMessagePreview,
    item?.last_message_preview,
  );

export const resolveChatFollowupBriefing = (item = {}) =>
  pickFirstText(
    item?.briefing,
    item?.briefing_text,
    item?.analysis,
    item?.analysisText,
    item?.analysis_text,
    item?.summary,
    item?.contextSummary,
    item?.context_summary,
  );

export const resolveChatFollowupAgeLabel = (item = {}) =>
  pickFirstText(item?.ageLabel, item?.age_label, item?.elapsedLabel, item?.elapsed_label);

export const resolveChatFollowupEvaluatedAt = (item = {}) =>
  pickFirstText(item?.evaluatedAt, item?.evaluated_at, item?.aiUpdatedAt, item?.ai_updated_at);

export const resolveChatFollowupItemId = (item = {}) =>
  pickFirstText(item?.id, item?.taskId, item?.task_id, item?.followupId, item?.followup_id);

export const resolveChatFollowupTargetAvatarUrl = (item = {}) =>
  normalizeApiAssetUrl(
    pickFirstText(item?.targetAvatarUrl, item?.target_avatar_url, item?.artistAvatarUrl, item?.artist_avatar_url)
  );

export const resolveChatFollowupTargetInitial = (item = {}) =>
  pickFirstText(item?.targetInitial, item?.target_initial, item?.artistInitial, item?.artist_initial);

export const resolveChatFollowupAssigneeAvatarUrl = (item = {}) =>
  normalizeApiAssetUrl(
    pickFirstText(
      item?.assigneeAvatarUrl,
      item?.assignee_avatar_url,
      item?.assignedAdminAvatarUrl,
      item?.assigned_admin_avatar_url
    )
  );

export const resolveChatFollowupAssigneeInitial = (item = {}) =>
  pickFirstText(
    item?.assigneeInitial,
    item?.assignee_initial,
    item?.assignedAdminInitial,
    item?.assigned_admin_initial
  );

export const useAdminChatFollowupSummary = ({
  enabled = true,
  refreshIntervalMs = CHAT_FOLLOWUP_SUMMARY_REFRESH_MS,
} = {}) => {
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(Boolean(enabled));

  const refresh = useCallback(async () => {
    if (!enabled) {
      setSummary(EMPTY_SUMMARY);
      setLoading(false);
      return EMPTY_SUMMARY;
    }

    setLoading(true);
    try {
      const nextSummary = await fetchAdminChatFollowupSummary();
      setSummary(nextSummary);
      return nextSummary;
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setSummary(EMPTY_SUMMARY);
      setLoading(false);
      return undefined;
    }

    let disposed = false;
    const load = async () => {
      setLoading(true);
      try {
        const nextSummary = await fetchAdminChatFollowupSummary();
        if (!disposed) {
          setSummary(nextSummary);
        }
      } finally {
        if (!disposed) {
          setLoading(false);
        }
      }
    };

    load();

    const intervalId =
      refreshIntervalMs > 0
        ? window.setInterval(() => {
            load();
          }, refreshIntervalMs)
        : null;

    const handleFocus = () => {
      load();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleFocus);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleFocus);
    }

    return () => {
      disposed = true;
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleFocus);
      }
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleFocus);
      }
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [enabled, refreshIntervalMs]);

  return {
    summary,
    badgeCount: extractChatFollowupOpenCount(summary),
    loading,
    refresh,
  };
};
